import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, Input, Renderer2, ViewChild } from '@angular/core';
import * as echarts from 'echarts';

interface VerticalChartData {
  label: string;
  amount: number;
}

@Component({
  selector: 'app-apache-vertical-chart',
  templateUrl: './apache-vertical-chart.component.html',
  styleUrls: ['./apache-vertical-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApacheVerticalChartComponent implements AfterViewInit {
  labels: string[] = [];
  isNoData = true;
  _data: VerticalChartData[] = [];
  _showTop10 = true;
  chart: any = undefined;

  @ViewChild('chart', { static: true }) chartElementRef: ElementRef;

  @Input() set showTop10(value: boolean) {
    this._showTop10 = value;
  }

  @Input() isForcedNoDataState = false;

  @Input() isSmallDevice = false;

  @Input() set verticalChartData(data: VerticalChartData[]) {
    const divElement = this.elementRef.nativeElement.querySelector('.chart-container');
    this.renderer.setStyle(divElement, 'height', this._setChartHeight(data));

    this._initializeChart();
    this._clearChart();
    this._resizeChart();
    this._data = [...data];
    this.labels = data.reduce((acc: string[], curr: VerticalChartData) => {
      acc.push(curr.label);
      return acc;
    }, []);
    if (this._data && this._data.length && this.chart && !this.isForcedNoDataState) {
      this.isNoData = false;
      this._renderChart(data);
    } else {
      this.isNoData = true;
      this._clearChart();
      this._resizeChart();
    }

    // removes cursor: pointer from chart series
    this.chart.on('mousemove', () => {
      this.chart.getZr().setCursorStyle('default');
    });
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    if (this.chart && this._data) {
      this.chart.resize();
    }
  }

  constructor(private renderer: Renderer2, private elementRef: ElementRef) {
  }

  ngAfterViewInit() {
    this._initializeChart();
  }

  private _renderChart(data): void {
    if (data) {
      const option = {
        xAxis: {
          max: 'dataMax',
          axisLabel: {
            formatter: function(value) {
              return Number.isInteger(value) ? value : undefined;
            }.bind(this),
          },
        },
        yAxis: {
          type: 'category',
          data: [...this.labels],
          inverse: true,
          animationDuration: 300,
          animationDurationUpdate: 300,
          max: data.length > 10 ? (this._showTop10 ? 9 : data.length - 1) : data.length - 1,
        },
        grid: {
          left: 80,
          right: 80,
          top: 30,
          bottom: 60,
        },
        series: [
          {
            realtimeSort: true,
            name: 'Visitors',
            type: 'bar',
            barWidth: '25px',
            data: data.reduce((acc, curr) => {
              acc.push(curr.amount);
              return acc;
            }, []),
            label: {
              show: true,
              position: 'right',
              valueAnimation: true,
            },
          },
        ],
      };

      this.chart.setOption(option);
    } else {
      this.chart.clear();
    }
    this._resizeChart();
  }

  private _initializeChart(): void {
    this.chart = echarts.init(this.chartElementRef.nativeElement, null, { renderer: 'svg' });
  }

  private _clearChart(): void {
    this.chart.clear();
  }

  private _resizeChart(): void {
    setTimeout(() => {
      this.chart.resize();
    }, 0);
  }

  showAll() {
    this._showTop10 = !this._showTop10;
    this.verticalChartData = this._data;
  }

  private _setChartHeight(data: any[]) {
    if (data.length < 10 || !this._showTop10) {
      return (data.length * 35) + 92 + 'px';
    } else {
      return '450px';
    }
  }
}
